import React from 'react'
import Checkbox from 'app/app/(client)/components/molecules/Checkbox/Checkbox'
import Text from 'app/app/(client)/components/atoms/Text/Text'
import NoSearchResults from '../../molecules/NoSearchResults/NoSearchResults'
import { capitalizeIfEnglish } from 'app/app/(client)/utils/utils'
import { isEven } from 'app/app/common/common-utils'
import { t } from 'i18next'

export interface SearchResult {
  id: string
  customerName: string
  opportunityName: string
  owner: string
  opportunityNumber: string
}

interface Props {
  searchResults: SearchResult[]
  opportunityIds: string[]
  buttonLoading: boolean
  allSelected: boolean
  toggleAll: () => void
  toggleRow: (id: string) => void
}

const OpportunityImportList: React.FC<Props> = ({
  searchResults,
  opportunityIds,
  buttonLoading,
  allSelected,
  toggleAll,
  toggleRow
}) => {
  const ResultRow: React.FC<{ result: SearchResult; index: number }> = ({ result, index }) => {
    const { id, customerName, opportunityName, owner, opportunityNumber } = result

    const handleCheckboxChange = (event: React.MouseEvent<HTMLDivElement>) => {
      event.stopPropagation()
      toggleRow(id)
    }

    return (
      <div
        key={id}
        className={`flex flex-row items-start md:items-center p-4 md:px-2 md:py-0 justify-center gap-4 md:gap-0 rounded-lg md:rounded-none w-full md:border md:border-b md:border-border-primary md:border-x-0 cursor-pointer ${
          isEven(index) ? 'bg-background-grey md:bg-background-grid-row' : 'bg-background-grey md:bg-white'
        }`}
        onClick={() => toggleRow(id)}
      >
        <div className="pt-1 md:pt-0 md:w-8 flex items-center justify-center">
          <Checkbox
            id={`import-opportunity-row-${id}`}
            checked={opportunityIds.includes(id)}
            onChecked={handleCheckboxChange}
            disabled={buttonLoading}
          />
        </div>

        <div className="flex flex-col md:flex-row w-full gap-2 md:gap-0 text-[14px]">
          <div className="grid grid-cols-[1fr_1.5fr] gap-2 md:block md:w-[40%] md:p-4">
            <Text className="font-bold text-grey-icon md:hidden" as="div">
              {capitalizeIfEnglish(t('customers.opportunity'))}
            </Text>
            <div className="flex flex-col">
              <div className="text-grey-icon font-semibold">{customerName}</div>
              <div className="text-grey-icon font-normal">{opportunityName}</div>
            </div>
          </div>

          <div className="grid grid-cols-[1fr_1.5fr] gap-2 md:block md:w-[30%] md:p-4 content-center">
            <Text className="font-bold text-grey-icon md:hidden" as="div">
              {capitalizeIfEnglish(t('customers.last.modified'))}
            </Text>
            <div className="text-grey-icon font-normal">{owner}</div>
          </div>

          <div className="grid grid-cols-[1fr_1.5fr] gap-2 md:block md:w-[30%] md:p-4 content-center">
            <Text className="font-bold text-grey-icon md:hidden" as="div">
              {capitalizeIfEnglish(t('customers.refresh.opportunityNumber'))}
            </Text>
            <div className="text-grey-icon font-normal">{opportunityNumber}</div>
          </div>
        </div>
      </div>
    )
  }

  if (searchResults.length === 0) {
    return <NoSearchResults className="h-55 w-62.5" />
  }

  return (
    <div className="w-full">
      <div className="hidden md:flex items-center w-full px-2 pb-2 font-bold border-b border-black">
        <div className="w-8 flex items-start justify-center">
          <Checkbox
            id="import-opportunity-select-all"
            checked={allSelected}
            onChecked={toggleAll}
            disabled={buttonLoading}
          />
        </div>
        <Text as="div" className="w-[40%] px-2 text-[16px]">
          {capitalizeIfEnglish(t('customers.opportunity'))}
        </Text>
        <Text as="div" className="w-[30%] px-2 text-[16px]">
          {capitalizeIfEnglish(t('customers.last.modified'))}
        </Text>
        <Text as="div" className="w-[30%] px-2 text-[16px]">
          {capitalizeIfEnglish(t('customers.refresh.opportunityNumber'))}
        </Text>
      </div>

      <div className="flex flex-col gap-4 md:gap-0 h-75 overflow-y-auto scrollbar--custom">
        {searchResults.map((rowDetails, index) => (
          <ResultRow key={rowDetails.id} result={rowDetails} index={index} />
        ))}
      </div>
    </div>
  )
}

export default OpportunityImportList
